import { FloatingWindowController } from "./floating-window-controller.js";
import {
  enhanceChartColorPickers,
  syncChartColorPicker
} from "../components/chart-color-picker.js";
import { createClientId } from "../core/client-id.js";
import { availabilityFromProviderEntitlement } from "../realtime/market-data-contracts.js";

const SORT_CYCLE = ["None", "Ascending", "Descending"];
const LIVE_PREFETCH_ROWS = 6;
const MAX_LIVE_WATCHLIST_ROWS = 48;

export class WatchlistRepository {
  constructor(endpoint = null) {
    this.setEndpoint(endpoint);
  }

  setEndpoint(endpoint) {
    this.endpoint = typeof endpoint === "string" && endpoint.trim()
      ? endpoint.trim().replace(/\/$/, "")
      : null;
  }

  list(signal) {
    return this.request("", { signal });
  }

  initializeDefault() {
    return this.request("/default", { method: "POST" });
  }

  create(name) {
    return this.request("", {
      method: "POST",
      body: { name }
    });
  }

  rename(watchlist) {
    return this.request(`/${watchlist.id}`, {
      method: "PATCH",
      body: { name: watchlist.name, expectedVersion: watchlist.version }
    });
  }

  delete(watchlist) {
    return this.request(
      `/${watchlist.id}?expectedVersion=${encodeURIComponent(watchlist.version)}`,
      { method: "DELETE", expectContent: false }
    );
  }

  addItem(watchlist, instrumentId, afterRowId = null) {
    return this.request(`/${watchlist.id}/items`, {
      method: "POST",
      body: { instrumentId, expectedVersion: watchlist.version, afterRowId }
    });
  }

  removeItem(watchlist, instrumentId) {
    return this.request(
      `/${watchlist.id}/items/${encodeURIComponent(instrumentId)}`
      + `?expectedVersion=${encodeURIComponent(watchlist.version)}`,
      { method: "DELETE" }
    );
  }

  moveItem(source, target, instrumentId, targetIndex) {
    return this.request(`/${source.id}/items/move`, {
      method: "POST",
      body: {
        instrumentId,
        targetWatchlistId: target.id,
        targetIndex,
        expectedSourceVersion: source.version,
        expectedTargetVersion: target.version
      }
    });
  }

  addRow(watchlist, rowType, afterRowId, sectionName = null) {
    return this.request(`/${watchlist.id}/rows`, {
      method: "POST",
      body: {
        rowType,
        afterRowId,
        sectionName,
        expectedVersion: watchlist.version
      }
    });
  }

  updateRow(watchlist, rowId, changes) {
    return this.request(`/${watchlist.id}/rows/${rowId}`, {
      method: "PATCH",
      body: { ...changes, expectedVersion: watchlist.version }
    });
  }

  removeRow(watchlist, rowId) {
    return this.request(
      `/${watchlist.id}/rows/${rowId}?expectedVersion=${encodeURIComponent(watchlist.version)}`,
      { method: "DELETE" }
    );
  }

  restoreRow(watchlist, row, targetIndex) {
    return this.request(`/${watchlist.id}/rows/restore`, {
      method: "POST",
      body: { row, targetIndex, expectedVersion: watchlist.version }
    });
  }

  moveRow(source, target, rowId, targetIndex) {
    return this.request(`/${source.id}/rows/move`, {
      method: "POST",
      body: {
        rowId,
        targetWatchlistId: target.id,
        targetIndex,
        expectedSourceVersion: source.version,
        expectedTargetVersion: target.version
      }
    });
  }

  saveSettings(watchlist, settings) {
    return this.request(`/${watchlist.id}/settings`, {
      method: "PUT",
      body: {
        ...settings,
        expectedVersion: watchlist.version
      }
    });
  }

  getWorkspace(signal) {
    return this.request("/workspace", { signal });
  }

  saveWorkspace(workspace) {
    return this.request("/workspace", {
      method: "PUT",
      body: {
        expectedVersion: workspace.version,
        panels: workspace.panels
      }
    });
  }

  async request(path, {
    method = "GET",
    body = null,
    signal = null,
    expectContent = true
  } = {}) {
    if (!this.endpoint) {
      throw new Error("Product-qualified watchlists are unavailable for this chart launch.");
    }
    const headers = {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {})
    };
    if (!["GET", "HEAD"].includes(method)) {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      if (token) headers["X-CSRF-TOKEN"] = token;
    }
    const response = await fetch(`${this.endpoint}${path}`, {
      method,
      credentials: "same-origin",
      headers,
      body: body ? JSON.stringify(body) : null,
      signal
    });
    if (!response.ok) {
      let detail = `Watchlist request failed with status ${response.status}.`;
      let code = null;
      try {
        const problem = await response.json();
        code = problem.code || null;
        detail = watchlistPolicyMessage(
          code,
          problem.detail || Object.values(problem.errors || {}).flat()[0] || detail
        );
      } catch {
        // The status code remains useful if the response body is not Problem Details.
      }
      const error = new Error(detail);
      error.name = "WatchlistRequestError";
      error.status = response.status;
      error.code = code;
      throw error;
    }
    if (!expectContent || response.status === 204) return null;
    return response.json();
  }
}

export class WatchlistManager {
  constructor({
    surface,
    template,
    openButton,
    symbolSearch,
    realtimeManager = null,
    repository = new WatchlistRepository(),
    onSelectInstrument = () => true,
    onFocusPanel = () => {},
    onWorkspaceChange = () => {},
    nextStackOrder = null,
    onAnnounce = () => {}
  }) {
    if (!(surface instanceof HTMLElement)) {
      throw new TypeError("WatchlistManager requires a workspace surface.");
    }
    if (!(template instanceof HTMLTemplateElement)) {
      throw new TypeError("WatchlistManager requires a window template.");
    }

    this.surface = surface;
    this.template = template;
    this.openButton = openButton;
    this.symbolSearch = symbolSearch;
    this.realtimeManager = realtimeManager;
    this.repository = repository;
    this.onSelectInstrument = onSelectInstrument;
    this.onFocusPanel = onFocusPanel;
    this.onWorkspaceChange = onWorkspaceChange;
    this.nextStackOrder = typeof nextStackOrder === "function"
      ? nextStackOrder
      : () => ++this.stackSequence;
    this.onAnnounce = onAnnounce;
    this.watchlists = new Map();
    this.panels = new Map();
    this.workspace = { version: 0, panels: [] };
    this.stackSequence = 30;
    this.dragPayload = null;
    this.dragPreview = null;
    this.saveTimer = null;
    this.saveChain = Promise.resolve();
    this.creatingWatchlist = false;
    this.destroyed = false;
    this.maximumItemsPerWatchlist = Number.POSITIVE_INFINITY;
    this.objectTreeListeners = new Set();
    this.boundDocumentPointerDown = (event) => this.handleDocumentPointerDown(event);
    document.addEventListener("pointerdown", this.boundDocumentPointerDown, true);
    this.openButton?.addEventListener("click", () => this.openFromToolbar());
  }

  configureProductPolicy({ endpoint, maximumItemsPerWatchlist = Number.POSITIVE_INFINITY }) {
    this.repository.setEndpoint(endpoint);
    this.maximumItemsPerWatchlist = Number.isFinite(maximumItemsPerWatchlist)
      ? Math.max(0, Number(maximumItemsPerWatchlist))
      : Number.POSITIVE_INFINITY;
    for (const panel of this.panels.values()) this.syncPanelQuota(panel);
  }

  async initialize() {
    let [watchlists, workspace] = await Promise.all([
      this.repository.list(),
      this.repository.getWorkspace()
    ]);
    if (!Array.isArray(watchlists)) {
      throw new Error("The product watchlist response is invalid.");
    }
    if (watchlists.length === 0) {
      const initialized = await this.repository.initializeDefault();
      if (!initialized?.id) {
        throw new Error("The default product watchlist could not be initialized.");
      }
      watchlists = [initialized];
    }
    this.replaceWatchlists(watchlists);
    this.workspace = normalizeWorkspace(workspace);

    const persistedPanels = this.workspace.panels
      .filter(panel => this.watchlists.has(panel.watchlistId))
      .slice(0, 12);
    if (persistedPanels.length > 0) {
      persistedPanels.forEach(panel => this.openPanel(panel.watchlistId, panel));
      const topmostPersisted = persistedPanels.reduce((topmost, candidate) =>
        Number(candidate.stackOrder) > Number(topmost.stackOrder)
          ? candidate
          : topmost);
      const topmostPanel = this.panels.get(topmostPersisted.id);
      if (topmostPanel) this.focusPanel(topmostPanel, { raise: false, persist: false });
      const savedSelections = persistedPanels.filter(panel =>
        panel.selectedInstrumentId);
      if (savedSelections.length > 1) {
        const topmostSelection = savedSelections.reduce((topmost, candidate) =>
          Number(candidate.stackOrder) > Number(topmost.stackOrder)
            ? candidate
            : topmost);
        const topmostPanel = this.panels.get(topmostSelection.id);
        if (topmostPanel) {
          const selectedRow = this.currentWatchlist(topmostPanel)?.rows.find(row =>
            row.id === topmostSelection.selectedRowId
            || (
              !topmostSelection.selectedRowId
              && row.instrumentId === topmostSelection.selectedInstrumentId
            ));
          if (selectedRow) {
            this.setSelectedRow(
              topmostPanel,
              selectedRow.id,
              selectedRow.instrumentId
            );
          }
        }
        this.scheduleWorkspaceSave();
      }
    }
    this.openButton?.removeAttribute("disabled");
    this.emitObjectTreeChange();
  }

  replaceWatchlists(watchlists) {
    this.watchlists.clear();
    for (const watchlist of watchlists || []) {
      this.watchlists.set(watchlist.id, normalizeWatchlist(watchlist));
    }
  }

  async openFromToolbar() {
    if (this.panels.size >= 12) {
      this.onAnnounce("You can open up to 12 watchlist windows.");
      return;
    }
    if (this.creatingWatchlist) return;
    this.creatingWatchlist = true;
    this.openButton?.setAttribute("disabled", "");
    try {
      await this.createWatchlist(this.nextWatchlistName());
    } finally {
      this.creatingWatchlist = false;
      this.openButton?.removeAttribute("disabled");
    }
  }

  openPanel(watchlistId, persisted = null) {
    const watchlist = this.watchlists.get(watchlistId);
    if (!watchlist || this.panels.size >= 12) return null;

    const fragment = this.template.content.cloneNode(true);
    const element = requiredElement("[data-watchlist-window]", fragment);
    const id = persisted?.id || createClientId();
    element.dataset.watchlistPanelId = id;
    const dragHandle = requiredElement("[data-window-drag-handle]", element);
    const resizeHandles = [...element.querySelectorAll("[data-window-resize]")];
    const title = requiredElement("[data-watchlist-title]", element);
    const count = requiredElement("[data-watchlist-count]", element);
    const body = requiredElement(".watchlist-body", element);
    const rows = requiredElement("[data-watchlist-rows]", element);
    const empty = requiredElement("[data-watchlist-empty]", element);
    const bottomAddButton = requiredElement("[data-watchlist-bottom-add]", element);
    const emptyAddButton = requiredElement("[data-watchlist-empty-add]", element);
    const rowMenu = requiredElement("[data-watchlist-row-menu]", element);
    const flagMenu = requiredElement("[data-watchlist-flag-menu]", element);
    const settingsDialog = requiredElement("[data-watchlist-settings-dialog]", element);
    const settingsDragHandle = requiredElement(
      "[data-watchlist-settings-drag-handle]",
      settingsDialog
    );
    for (const density of settingsDialog.querySelectorAll("[data-watchlist-density]")) {
      density.name = `watchlist-density-${id}`;
    }
    const undoToast = requiredElement("[data-watchlist-undo-toast]", element);
    const live = requiredElement("[data-watchlist-live]", element);
    const quoteStatus = requiredElement("[data-watchlist-quote-status]", element);
    const minimizeButton = requiredElement("[data-window-minimize]", element);
    const maximizeButton = requiredElement("[data-window-maximize]", element);
    const restoreButton = requiredElement("[data-window-restore]", element);
    const closeButton = requiredElement("[data-window-close]", element);
    const dropEnd = requiredElement("[data-watchlist-drop-end]", element);

    this.surface.append(element);
    let panel = null;
    const floating = new FloatingWindowController({
      surface: this.surface,
      windowElement: element,
      dragHandle,
      resizeHandles,
      onInteractionEnd: () => {
        if (panel) {
          this.scheduleWorkspaceSave();
          this.scheduleRealtimeSync(panel);
        }
      }
    });
    panel = {
      id,
      watchlistId,
      element,
      title,
      count,
      body,
      rows,
      empty,
      bottomAddButton,
      rowMenu,
      flagMenu,
      flagMenuCloseTimer: null,
      rowMenuInstrumentId: null,
      rowMenuTarget: null,
      settingsDialog,
      settingsDragHandle,
      settingsDrag: null,
      settingsColorPickers: enhanceChartColorPickers(settingsDialog),
      settingsReturnFocus: null,
      settingsRevision: 0,
      settingsSaveTimer: null,
      settingsSaveChain: Promise.resolve(),
      undoToast,
      undoState: null,
      undoTimer: null,
      live,
      quoteStatus,
      minimizeButton,
      maximizeButton,
      restoreButton,
      closeButton,
      dropEnd,
      floating,
      sortColumn: persisted?.sortColumn || "Manual",
      sortDirection: persisted?.sortDirection || "None",
      selectedInstrumentId: persisted?.selectedInstrumentId || null,
      selectedRowId: persisted?.selectedRowId || null,
      stackOrder: persisted?.stackOrder || ++this.stackSequence,
      quoteMap: new Map(),
      realtimeReleases: new Map(),
      realtimeStatuses: new Map(),
      realtimeProviderStatuses: new Map(),
      realtimeSyncFrame: null
    };
    this.panels.set(id, panel);
    this.emitObjectTreeChange();
    if (panel.selectedInstrumentId || panel.selectedRowId) {
      const selectedRow = watchlist.rows.find(row =>
        row.id === panel.selectedRowId
        || (!panel.selectedRowId && row.instrumentId === panel.selectedInstrumentId));
      if (selectedRow) {
        this.setSelectedRow(panel, selectedRow.id, selectedRow.instrumentId);
      }
    }

    if (persisted) {
      floating.state = "floating";
      element.dataset.windowState = "floating";
      floating.applyGeometry({
        x: persisted.x,
        y: persisted.y,
        width: persisted.width,
        height: persisted.height
      });
      if (persisted.windowState === "Minimized") floating.minimize();
      if (persisted.windowState === "Maximized") floating.maximize();
    } else {
      const offset = this.panels.size - 1;
      floating.applyGeometry({
        x: 38 + offset * 24,
        y: 44 + offset * 24,
        width: Math.min(560, Math.max(320, this.surface.clientWidth - 28)),
        height: Math.min(520, Math.max(240, this.surface.clientHeight - 28))
      });
    }
    element.style.zIndex = String(panel.stackOrder);

    element.addEventListener("pointerdown", () => {
      this.focusPanel(panel);
      element.focus({ preventScroll: true });
    }, { capture: true });
    element.addEventListener("keydown", event => this.handlePanelTypeahead(panel, event));
    bottomAddButton.addEventListener("click", () =>
      this.openAddSymbol(panel, bottomAddButton));
    emptyAddButton.addEventListener("click", () => this.openAddSymbol(panel, emptyAddButton));
    for (const action of rowMenu.querySelectorAll("[data-watchlist-row-action]")) {
      action.addEventListener("click", () =>
        this.handleRowMenuAction(panel, action.dataset.watchlistRowAction));
    }
    for (const action of flagMenu.querySelectorAll("[data-watchlist-flag-color]")) {
      action.addEventListener("click", () =>
        this.setContextFlag(panel, action.dataset.watchlistFlagColor));
    }
    const flagAction = rowMenu.querySelector('[data-watchlist-row-action="flag"]');
    flagAction.addEventListener("pointerenter", () => this.openFlagMenu(panel, false));
    rowMenu.addEventListener("pointerenter", () => this.cancelFlagMenuClose(panel));
    rowMenu.addEventListener("pointerleave", event => {
      if (!flagMenu.contains(event.relatedTarget)) this.scheduleFlagMenuClose(panel);
    });
    flagMenu.addEventListener("pointerenter", () => this.cancelFlagMenuClose(panel));
    flagMenu.addEventListener("pointerleave", event => {
      if (!rowMenu.contains(event.relatedTarget)) this.scheduleFlagMenuClose(panel);
    });
    flagMenu.addEventListener("keydown", event => this.handleFlagMenuKeydown(panel, event));
    settingsDialog.addEventListener("keydown", event => this.handleSettingsKeydown(panel, event));
    settingsDialog.addEventListener("input", () => this.previewSettings(panel));
    settingsDialog.addEventListener("change", () => this.previewSettings(panel));
    settingsDialog.addEventListener("chart-color-opacity", () => this.previewSettings(panel));
    settingsDragHandle.addEventListener(
      "pointerdown",
      event => this.startSettingsDrag(panel, event)
    );
    settingsDragHandle.addEventListener(
      "pointermove",
      event => this.moveSettingsDrag(panel, event)
    );
    settingsDragHandle.addEventListener(
      "pointerup",
      event => this.stopSettingsDrag(panel, event)
    );
    settingsDragHandle.addEventListener(
      "pointercancel",
      event => this.stopSettingsDrag(panel, event)
    );
    requiredElement("[data-watchlist-settings-close]", settingsDialog)
      .addEventListener("click", () => this.closeSettings(panel));
    requiredElement("[data-watchlist-settings-reset]", settingsDialog)
      .addEventListener("click", () => this.resetSettingsDraft(panel));
    requiredElement("[data-watchlist-settings-done]", settingsDialog)
      .addEventListener("click", () => this.commitAndCloseSettings(panel));
    requiredElement("[data-watchlist-undo]", undoToast)
      .addEventListener("click", () => this.undoDelete(panel));
    minimizeButton.addEventListener("click", () => {
      floating.minimize();
      this.scheduleRealtimeSync(panel);
      this.updateWindowControls(panel);
      this.scheduleWorkspaceSave();
      this.emitObjectTreeChange();
    });
    maximizeButton.addEventListener("click", () => {
      floating.maximize();
      this.scheduleRealtimeSync(panel);
      this.updateWindowControls(panel);
      this.scheduleWorkspaceSave();
      this.emitObjectTreeChange();
    });
    restoreButton.addEventListener("click", () => {
      floating.restore();
      this.scheduleRealtimeSync(panel);
      this.updateWindowControls(panel);
      this.scheduleWorkspaceSave();
      this.emitObjectTreeChange();
    });
    closeButton.addEventListener("click", () => this.closePanel(panel));
    for (const sortButton of element.querySelectorAll("[data-watchlist-sort]")) {
      sortButton.addEventListener("click", () =>
        this.cycleSort(panel, sortButton.dataset.watchlistSort));
    }
    rows.addEventListener("keydown", event => this.handleRowKeydown(panel, event));
    rows.addEventListener("dblclick", event => {
      const row = event.target.closest('tr[data-watchlist-row-kind="section"]');
      if (row) this.beginSectionRename(panel, row.dataset.watchlistRowId);
    });
    body.addEventListener("contextmenu", event => this.openRowMenu(panel, event));
    body.addEventListener("scroll", () => this.scheduleRealtimeSync(panel), { passive: true });
    rowMenu.addEventListener("keydown", event => this.handleRowMenuKeydown(panel, event));
    dropEnd.addEventListener("dragover", event => this.allowDrop(panel, event, dropEnd));
    dropEnd.addEventListener("drop", event =>
      this.dropItem(panel, event, this.currentWatchlist(panel)?.rows.length || 0));
    body.addEventListener("dragover", event => {
      if (event.target.closest("tr[data-watchlist-row-id], [data-watchlist-drop-end]")) {
        return;
      }
      this.allowDrop(panel, event, dropEnd);
    });
    body.addEventListener("drop", event => {
      if (event.target.closest("tr[data-watchlist-row-id], [data-watchlist-drop-end]")) {
        return;
      }
      this.dropItem(panel, event, this.currentWatchlist(panel)?.rows.length || 0);
    });

    this.updateWindowControls(panel);
    this.renderPanel(panel);
    if (!persisted) this.focusPanel(panel);
    if (!persisted) {
      this.scheduleWorkspaceSave();
    }
    return panel;
  }

  focusPanel(panel, { raise = true, persist = true } = {}) {
    if (!panel || !this.panels.has(panel.id)) return;
    this.onFocusPanel(panel);
    for (const candidate of this.panels.values()) {
      candidate.element.classList.toggle("is-active", candidate === panel);
    }
    if (raise) panel.stackOrder = this.nextStackOrder();
    panel.element.style.zIndex = String(panel.stackOrder);
    this.emitObjectTreeChange();
    if (persist) this.scheduleWorkspaceSave();
  }

  clearFocus() {
    for (const panel of this.panels.values()) {
      panel.element.classList.remove("is-active");
      if (
        document.activeElement instanceof HTMLElement
        && panel.element.contains(document.activeElement)
      ) {
        document.activeElement.blur();
      }
    }
  }

  closePanel(panel) {
    this.releasePanelRealtime(panel);
    if (panel.realtimeSyncFrame !== null) cancelAnimationFrame(panel.realtimeSyncFrame);
    window.clearTimeout(panel.undoTimer);
    window.clearTimeout(panel.settingsSaveTimer);
    if (panel.settingsRevision > 0) void this.flushSettings(panel);
    for (const picker of panel.settingsColorPickers) picker.destroy();
    panel.floating.dispose();
    panel.element.remove();
    this.panels.delete(panel.id);
    this.scheduleWorkspaceSave();
    this.emitObjectTreeChange();
  }

  currentWatchlist(panel) {
    return this.watchlists.get(panel.watchlistId) || null;
  }

  renderPanel(panel) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    panel.title.textContent = watchlist.name;
    panel.count.textContent = String(watchlist.items.length);
    panel.element.dataset.density = watchlist.settings.density;
    panel.element.classList.toggle("hide-symbol", !watchlist.settings.showSymbol);
    panel.element.classList.toggle("hide-last", !watchlist.settings.showLast);
    panel.element.classList.toggle("hide-change", !watchlist.settings.showChange);
    panel.element.classList.toggle("hide-percent", !watchlist.settings.showPercentChange);
    panel.element.classList.toggle("hide-volume", !watchlist.settings.showVolume);
    panel.element.classList.toggle("hide-name", !watchlist.settings.showName);
    panel.element.classList.toggle("hide-sector", !watchlist.settings.showSector);
    panel.element.classList.toggle("hide-industry", !watchlist.settings.showIndustry);
    panel.element.style.setProperty("--watchlist-positive", watchlist.settings.positiveColor);
    panel.element.style.setProperty("--watchlist-negative", watchlist.settings.negativeColor);
    const tableWidth = (watchlist.settings.showSymbol ? 104 : 0)
      + (watchlist.settings.showName ? 144 : 0)
      + (watchlist.settings.showLast ? 68 : 0)
      + (watchlist.settings.showChange ? 68 : 0)
      + (watchlist.settings.showPercentChange ? 72 : 0)
      + (watchlist.settings.showVolume ? 88 : 0)
      + (watchlist.settings.showSector ? 112 : 0)
      + (watchlist.settings.showIndustry ? 144 : 0);
    panel.element.style.setProperty(
      "--watchlist-table-min-width",
      `${Math.max(300, tableWidth)}px`
    );
    panel.element.setAttribute(
      "aria-label",
      `${watchlist.name} watchlist, ${watchlist.items.length} instruments`
    );
    this.emitObjectTreeChange();
    this.renderRows(panel);
    this.renderSortHeaders(panel);
    this.syncPanelQuota(panel);
    this.scheduleRealtimeSync(panel);
  }

  renderRows(panel, { preserveFocus = false } = {}) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    const focusedRowId = preserveFocus
      ? panel.rows.querySelector("tr:focus")?.dataset.watchlistRowId
      : null;
    panel.rows.replaceChildren();
    const items = this.sortedRows(panel, watchlist);
    let collapsed = false;
    for (const [index, item] of items.entries()) {
      if (item.rowType === "Section") {
        collapsed = item.isCollapsed;
      } else if (collapsed) {
        continue;
      }
      panel.rows.append(item.rowType === "Symbol"
        ? this.createRow(panel, item, index)
        : this.createDecorativeRow(panel, item, index));
    }
    panel.empty.hidden = items.length > 0;
    panel.element.classList.toggle("is-empty", items.length === 0);
    this.updateQuoteCells(panel);
    this.scheduleRealtimeSync(panel);
    if (focusedRowId) {
      panel.rows.querySelector(
        `tr[data-watchlist-row-id="${CSS.escape(focusedRowId)}"]`
      )?.focus();
    }
  }

  createRow(panel, item, index) {
    const row = document.createElement("tr");
    row.dataset.instrumentId = item.instrumentId;
    row.dataset.watchlistRowId = item.id;
    row.dataset.watchlistRowKind = "symbol";
    row.dataset.rowIndex = String(index);
    if (item.flagColor) row.dataset.flagColor = item.flagColor;
    row.setAttribute("role", "row");
    row.setAttribute("aria-rowindex", String(index + 2));
    const selected = item.id === panel.selectedRowId
      || (!panel.selectedRowId && item.instrumentId === panel.selectedInstrumentId);
    row.tabIndex = selected
      || (!panel.selectedRowId && !panel.selectedInstrumentId && index === 0) ? 0 : -1;
    row.setAttribute("aria-selected", String(selected));
    row.classList.toggle("is-flagged", Boolean(item.flagColor));
    row.innerHTML = `
      <th scope="row" data-watchlist-column="symbol">
        <span class="watchlist-symbol-cell">
          <span class="watchlist-drag-grip" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><i></i></span>
          <span class="watchlist-flag" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M5 21V4m0 0h11l-2 4 2 4H5"></path></svg>
          </span>
          <span class="watchlist-symbol">
            <strong></strong>
          </span>
        </span>
      </th>
      <td data-watchlist-column="name"></td>
      <td data-watchlist-column="last" data-quote-field="last">—</td>
      <td data-watchlist-column="change" data-quote-field="change">—</td>
      <td data-watchlist-column="percentChange" data-quote-field="percentChange">—</td>
      <td data-watchlist-column="volume" data-quote-field="volume">—</td>
      <td data-watchlist-column="sector"></td>
      <td data-watchlist-column="industry"></td>`;
    row.querySelector("strong").textContent = item.symbol;
    row.querySelector('[data-watchlist-column="name"]').textContent = item.displayName || "—";
    const classification = classifyInstrument(item);
    row.querySelector('[data-watchlist-column="sector"]').textContent = classification.sector;
    row.querySelector('[data-watchlist-column="industry"]').textContent = classification.industry;
    const grip = row.querySelector(".watchlist-drag-grip");
    const manualOrder = panel.sortDirection === "None";
    row.draggable = manualOrder;
    row.classList.toggle("is-reorder-enabled", manualOrder);
    grip.setAttribute("role", "button");
    grip.setAttribute(
      "aria-label",
      manualOrder
        ? `Drag ${item.symbol} to reorder or move it`
        : `Reordering ${item.symbol} is disabled while sorted`
    );
    grip.title = manualOrder
      ? "Drag to reorder or move to another watchlist"
      : "Return to manual order to rearrange";
    grip.classList.toggle("is-disabled", !manualOrder);
    row.addEventListener("click", () => {
      if (panel.suppressClickRowId === item.id) return;
      row.focus();
      this.selectRow(panel, item.id, true);
    });
    row.addEventListener("focus", () => {
      for (const candidate of panel.rows.querySelectorAll("tr")) {
        candidate.tabIndex = candidate === row ? 0 : -1;
      }
    });
    row.addEventListener("dragstart", event => this.startDrag(panel, item, event));
    row.addEventListener("dragend", () => this.endDrag(panel, item.id));
    row.addEventListener("dragover", event => this.allowDrop(panel, event, row));
    row.addEventListener("dragleave", event => {
      if (!row.contains(event.relatedTarget)) row.classList.remove("is-drop-target");
    });
    row.addEventListener("drop", event => this.dropItem(panel, event, index));
    return row;
  }

  createDecorativeRow(panel, item, index) {
    const row = document.createElement("tr");
    const kind = item.rowType.toLowerCase();
    row.dataset.watchlistRowId = item.id;
    row.dataset.watchlistRowKind = kind;
    row.dataset.rowIndex = String(index);
    row.className = `watchlist-decoration-row is-${kind}`;
    row.setAttribute("role", "row");
    row.setAttribute("aria-rowindex", String(index + 2));
    row.tabIndex = -1;
    row.setAttribute(
      "aria-label",
      kind === "section" ? `${item.sectionName} section` : "Empty watchlist row"
    );
    const cell = document.createElement("td");
    cell.colSpan = 8;
    if (kind === "section") {
      row.dataset.collapsed = String(item.isCollapsed);
      const sectionRows = this.sectionRows(this.currentWatchlist(panel), item.id);
      const count = sectionRows.filter(candidate => candidate.rowType === "Symbol").length;
      const content = document.createElement("div");
      content.className = "watchlist-section-content";
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "watchlist-section-toggle";
      toggle.setAttribute("aria-label", `${item.isCollapsed ? "Expand" : "Collapse"} ${item.sectionName}`);
      toggle.setAttribute("aria-expanded", String(!item.isCollapsed));
      toggle.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"></path></svg>';
      const label = document.createElement("strong");
      label.className = "watchlist-section-name";
      label.textContent = item.sectionName;
      const badge = document.createElement("span");
      badge.className = "watchlist-section-count";
      badge.textContent = String(count);
      badge.hidden = !this.currentWatchlist(panel).settings.showSectionCounts;
      content.append(toggle, label, badge);
      cell.append(content);
      toggle.addEventListener("click", event => {
        event.stopPropagation();
        this.toggleSection(panel, item.id, !item.isCollapsed);
      });
    } else {
      cell.innerHTML = '<span class="watchlist-empty-row-mark" aria-hidden="true"></span>';
    }
    row.append(cell);
    const manualOrder = panel.sortDirection === "None";
    row.draggable = manualOrder;
    row.classList.toggle("is-reorder-enabled", manualOrder);
    row.addEventListener("click", () => row.focus());
    row.addEventListener("dragstart", event => this.startDrag(panel, item, event));
    row.addEventListener("dragend", () => this.endDrag(panel, item.id));
    row.addEventListener("dragover", event => this.allowDrop(panel, event, row));
    row.addEventListener("dragleave", event => {
      if (!row.contains(event.relatedTarget)) row.classList.remove("is-drop-target");
    });
    row.addEventListener("drop", event => this.dropItem(panel, event, index));
    return row;
  }

  updateQuoteCells(panel) {
    for (const row of panel.rows.querySelectorAll("tr[data-instrument-id]")) {
      const quote = panel.quoteMap.get(row.dataset.instrumentId);
      const last = row.querySelector('[data-quote-field="last"]');
      const change = row.querySelector('[data-quote-field="change"]');
      const percent = row.querySelector('[data-quote-field="percentChange"]');
      const volume = row.querySelector('[data-quote-field="volume"]');
      const liveStatus = panel.realtimeStatuses.get(row.dataset.instrumentId);
      row.dataset.marketDataStatus = liveStatus || "offline";
      if (!quote?.isAvailable || quote.last === null) {
        last.textContent = "—";
        change.textContent = "—";
        percent.textContent = "—";
        volume.textContent = "—";
        last.setAttribute("aria-label", "Last price unavailable");
        change.setAttribute("aria-label", "Price change unavailable");
        percent.setAttribute("aria-label", "Percent change unavailable");
        volume.setAttribute("aria-label", "Volume unavailable");
        row.classList.add("quote-unavailable");
        row.title = quote?.unavailableReason || marketDataStatusDetail(liveStatus);
        continue;
      }
      row.classList.remove("quote-unavailable");
      const bidAsk = Number.isFinite(Number(quote.bid)) || Number.isFinite(Number(quote.ask))
        ? ` Bid ${formatPrice(quote.bid)} · Ask ${formatPrice(quote.ask)}.`
        : "";
      row.title = `${marketDataStatusDetail(liveStatus)}${bidAsk}`;
      last.textContent = formatPrice(quote.last);
      change.textContent = formatSigned(quote.change);
      percent.textContent = quote.percentChange === null
        ? "—"
        : `${formatSigned(quote.percentChange)}%`;
      volume.textContent = formatVolume(quote.volume);
      const direction = Number(quote.change) > 0 ? "up" : Number(quote.change) < 0 ? "down" : "flat";
      change.dataset.direction = direction;
      percent.dataset.direction = direction;
      const directionLabel = direction === "up"
        ? "increased"
        : direction === "down" ? "decreased" : "unchanged";
      last.setAttribute("aria-label", `Last price ${last.textContent}`);
      change.setAttribute(
        "aria-label",
        `Price ${directionLabel} by ${change.textContent}`
      );
      percent.setAttribute(
        "aria-label",
        `Price ${directionLabel} by ${percent.textContent}`
      );
      volume.setAttribute("aria-label", `Volume ${volume.textContent}`);
    }
  }

  scheduleRealtimeSync(panel) {
    if (!this.realtimeManager || panel.realtimeSyncFrame !== null || !this.panels.has(panel.id)) return;
    panel.realtimeSyncFrame = requestAnimationFrame(() => {
      panel.realtimeSyncFrame = null;
      this.syncPanelRealtime(panel);
    });
  }

  syncPanelRealtime(panel) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist || panel.element.dataset.windowState === "minimized") {
      this.releasePanelRealtime(panel);
      return;
    }
    const visibleIds = visibleInstrumentIds(panel, LIVE_PREFETCH_ROWS, MAX_LIVE_WATCHLIST_ROWS);
    const desiredKeys = new Set();
    for (const instrumentId of visibleIds) {
      const item = watchlist.rows.find(row => row.rowType === "Symbol" && row.instrumentId === instrumentId);
      if (!item) continue;
      const type = "PRICEDATA";
      const key = `${instrumentId}|${type}`;
      desiredKeys.add(key);
      if (panel.realtimeReleases.has(key)) continue;
      const handle = this.realtimeManager.subscribe({
        internalSymbolId: instrumentId,
        providerSymbol: item.providerSymbol || item.symbol,
        type,
        conflation: null,
        skipHeavyInitialLoad: true,
        exchange: item.exchangeCode,
        expectedAvailability: item.dataAvailability
      }, event => this.handleWatchlistRealtimeEvent(panel, instrumentId, type, event));
      panel.realtimeReleases.set(key, handle.release);
    }
    for (const [key, release] of panel.realtimeReleases) {
      if (desiredKeys.has(key)) continue;
      release();
      panel.realtimeReleases.delete(key);
      panel.realtimeProviderStatuses.delete(key);
    }
    for (const instrumentId of panel.realtimeStatuses.keys()) {
      if (!visibleIds.includes(instrumentId)) panel.realtimeStatuses.delete(instrumentId);
    }
  }

  handleWatchlistRealtimeEvent(panel, instrumentId, type, event) {
    if (!this.panels.has(panel.id)) return;
    if (event.kind === "subscription-status") {
      const status = event.status === "subscribed"
        ? availabilityFromProviderEntitlement(event.metadata?.entitlement) || "offline"
        : event.status === "unentitled"
          ? "not-entitled"
          : "offline";
      panel.realtimeProviderStatuses.set(`${instrumentId}|${type}`, status);
      const aggregate = panel.realtimeProviderStatuses.get(`${instrumentId}|PRICEDATA`)
        || "offline";
      panel.realtimeStatuses.set(instrumentId, aggregate);
      this.updateQuoteCells(panel);
      return;
    }
    const previous = panel.quoteMap.get(instrumentId) || {};
    panel.quoteMap.set(instrumentId, {
      ...previous,
      instrumentId,
      isAvailable: true,
      last: event.price ?? previous.last ?? null,
      change: event.change ?? previous.change ?? null,
      percentChange: event.percentChange ?? previous.percentChange ?? null,
      volume: event.volume ?? previous.volume ?? null,
      bid: event.bid ?? previous.bid ?? null,
      ask: event.ask ?? previous.ask ?? null,
      asOfUtc: event.eventTimestampUtc || event.receiptTimestampUtc
    });
    this.updateQuoteCells(panel);
  }

  releasePanelRealtime(panel) {
    for (const release of panel.realtimeReleases.values()) release();
    panel.realtimeReleases.clear();
    panel.realtimeStatuses.clear();
    panel.realtimeProviderStatuses.clear();
  }

  setRealtimeConnectionState(event) {
    const state = event?.state;
    const label = state === "Connected"
      ? "Live visible-row quotes"
      : state === "Reconnecting"
        ? "Reconnecting live quotes"
      : state === "Unauthorized"
          ? "Live quote access unavailable"
          : "Live quotes offline";
    for (const panel of this.panels.values()) {
      panel.quoteStatus.textContent = label;
      if (state === "Reconnecting") {
        for (const instrumentId of panel.realtimeStatuses.keys()) {
          panel.realtimeStatuses.set(instrumentId, "reconnecting");
        }
      }
      this.updateQuoteCells(panel);
    }
  }

  sortedRows(panel, watchlist) {
    const items = [...watchlist.rows].sort((a, b) => a.manualOrder - b.manualOrder);
    if (panel.sortDirection === "None" || panel.sortColumn === "Manual") {
      return items;
    }
    const direction = panel.sortDirection === "Ascending" ? 1 : -1;
    const compareSymbols = (a, b) => {
      let comparison = 0;
      if (panel.sortColumn === "Symbol") {
        comparison = a.symbol.localeCompare(b.symbol);
      } else if (["Name", "Sector", "Industry"].includes(panel.sortColumn)) {
        const field = panel.sortColumn.toLowerCase();
        const aValue = field === "name"
          ? a.displayName
          : classifyInstrument(a)[field];
        const bValue = field === "name"
          ? b.displayName
          : classifyInstrument(b)[field];
        comparison = String(aValue || "").localeCompare(String(bValue || ""));
      } else {
        const field = {
          Last: "last",
          Change: "change",
          PercentChange: "percentChange",
          Volume: "volume"
        }[panel.sortColumn];
        const aValue = panel.quoteMap.get(a.instrumentId)?.[field];
        const bValue = panel.quoteMap.get(b.instrumentId)?.[field];
        const aMissing = aValue === null || aValue === undefined;
        const bMissing = bValue === null || bValue === undefined;
        if (aMissing !== bMissing) return aMissing ? 1 : -1;
        if (!aMissing && !bMissing) comparison = Number(aValue) - Number(bValue);
      }
      return comparison === 0
        ? a.manualOrder - b.manualOrder
        : comparison * direction;
    };
    const result = [];
    let symbolBlock = [];
    const flush = () => {
      result.push(...symbolBlock.sort(compareSymbols));
      symbolBlock = [];
    };
    for (const row of items) {
      if (row.rowType === "Symbol") {
        symbolBlock.push(row);
      } else {
        flush();
        result.push(row);
      }
    }
    flush();
    return result;
  }

  sectionRows(watchlist, sectionId) {
    const ordered = [...(watchlist?.rows || [])].sort(
      (a, b) => a.manualOrder - b.manualOrder
    );
    const index = ordered.findIndex(row => row.id === sectionId);
    if (index < 0) return [];
    const result = [];
    for (let cursor = index + 1; cursor < ordered.length; cursor += 1) {
      if (ordered[cursor].rowType === "Section") break;
      result.push(ordered[cursor]);
    }
    return result;
  }

  cycleSort(panel, column) {
    if (panel.sortColumn !== column) {
      panel.sortColumn = column;
      panel.sortDirection = "Ascending";
    } else {
      const index = SORT_CYCLE.indexOf(panel.sortDirection);
      panel.sortDirection = SORT_CYCLE[(index + 1) % SORT_CYCLE.length];
      if (panel.sortDirection === "None") panel.sortColumn = "Manual";
    }
    this.renderRows(panel, { preserveFocus: true });
    this.renderSortHeaders(panel);
    this.scheduleWorkspaceSave();
  }

  renderSortHeaders(panel) {
    for (const button of panel.element.querySelectorAll("[data-watchlist-sort]")) {
      const active = panel.sortColumn === button.dataset.watchlistSort
        && panel.sortDirection !== "None";
      button.dataset.direction = active ? panel.sortDirection : "None";
      button.closest("th")?.setAttribute(
        "aria-sort",
        active ? panel.sortDirection.toLowerCase() : "none"
      );
    }
  }

  selectRow(panel, rowId, activate) {
    const watchlist = this.currentWatchlist(panel);
    const item = watchlist?.rows.find(candidate =>
      candidate.id === rowId && candidate.rowType === "Symbol");
    if (!item) return;
    this.setSelectedRow(panel, rowId, item.instrumentId);
    this.scheduleWorkspaceSave();
    if (!activate) return;

    const outcome = this.onSelectInstrument(toInstrument(item));
    if (outcome === false || outcome?.accepted === false) {
      this.announce(panel, outcome?.message || "The symbol could not be loaded.");
      return;
    }
    this.announce(panel, `${item.symbol} loaded in the linked chart group.`);
  }

  setSelectedRow(panel, rowId, instrumentId) {
    for (const candidate of this.panels.values()) {
      if (candidate !== panel) {
        candidate.selectedInstrumentId = null;
        candidate.selectedRowId = null;
      }
      for (const row of candidate.rows.querySelectorAll("tr[data-instrument-id]")) {
        const selected = candidate === panel
          && row.dataset.watchlistRowId === rowId;
        row.setAttribute("aria-selected", String(selected));
        if (candidate === panel) {
          row.tabIndex = selected ? 0 : -1;
        }
      }
    }
    panel.selectedInstrumentId = instrumentId;
    panel.selectedRowId = rowId;
  }

  focusAndRevealRow(panel, rowId, { activate = false, flash = false } = {}) {
    const row = panel.rows.querySelector(
      `tr[data-watchlist-row-id="${CSS.escape(rowId)}"]`
    );
    if (!row) return null;
    row.scrollIntoView({ block: "nearest" });
    row.focus({ preventScroll: true });
    this.selectRow(panel, rowId, activate);
    if (flash) {
      row.classList.remove("is-duplicate");
      requestAnimationFrame(() => row.classList.add("is-duplicate"));
      window.setTimeout(() => row.classList.remove("is-duplicate"), 1_400);
    }
    return row;
  }

  focusRowFromKeyboard(panel, row) {
    if (!row) return;
    for (const candidate of panel.rows.querySelectorAll("tr[data-watchlist-row-id]")) {
      const focused = candidate === row;
      candidate.tabIndex = focused ? 0 : -1;
      candidate.classList.toggle("is-keyboard-focused", focused);
    }
    row.focus();
  }

  handleRowKeydown(panel, event) {
    const row = event.target.closest("tr[data-watchlist-row-id]");
    if (!row) return;
    const rows = [...panel.rows.querySelectorAll("tr[data-watchlist-row-id]")];
    const index = rows.indexOf(row);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      const target = rows[Math.max(0, Math.min(rows.length - 1, index + delta))];
      this.focusRowFromKeyboard(panel, target);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (row.dataset.watchlistRowKind === "symbol") {
        this.selectRow(panel, row.dataset.watchlistRowId, true);
      } else if (row.dataset.watchlistRowKind === "section") {
        this.toggleSection(
          panel,
          row.dataset.watchlistRowId,
          row.dataset.collapsed !== "true"
        );
      }
      return;
    }
    if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) {
      event.preventDefault();
      this.openRowMenu(panel, event, row);
      return;
    }
    if (event.key === " ") {
      event.preventDefault();
      if (row.dataset.watchlistRowKind === "section") {
        this.toggleSection(
          panel,
          row.dataset.watchlistRowId,
          row.dataset.collapsed !== "true"
        );
        return;
      }
      const delta = event.shiftKey ? -1 : 1;
      const target = rows[Math.max(0, Math.min(rows.length - 1, index + delta))];
      if (!target) return;
      this.focusRowFromKeyboard(panel, target);
      if (target.dataset.watchlistRowKind === "symbol") {
        this.selectRow(panel, target.dataset.watchlistRowId, true);
      }
      return;
    }
    if (
      row.dataset.watchlistRowKind === "section"
      && (event.key === "ArrowLeft" || event.key === "ArrowRight")
    ) {
      event.preventDefault();
      this.toggleSection(
        panel,
        row.dataset.watchlistRowId,
        event.key === "ArrowLeft"
      );
      return;
    }
    if (event.key === "F2" && row.dataset.watchlistRowKind === "section") {
      event.preventDefault();
      this.beginSectionRename(panel, row.dataset.watchlistRowId);
    }
  }

  handlePanelTypeahead(panel, event) {
    const target = event.target;
    if (
      event.defaultPrevented
      || event.isComposing
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.key.length !== 1
      || !/^[a-z0-9.^$/@~_-]$/i.test(event.key)
      || (
        target instanceof Element
        && target.closest("input, textarea, select, [contenteditable='true'], [role='menu'], [role='dialog']")
      )
    ) return;

    event.preventDefault();
    event.stopPropagation();
    this.openAddSymbol(panel, elementTrigger(panel.element, target), event.key);
  }

  openAddSymbol(panel, trigger, query = "") {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    const quotaReason = this.watchlistQuotaReason(watchlist);
    if (quotaReason) {
      this.announce(panel, quotaReason);
      this.syncPanelQuota(panel);
      return;
    }
    this.closeMenus();
    this.symbolSearch.open({
      query,
      trigger,
      selectQuery: query.length === 0,
      modeLabel: `Add a symbol to ${watchlist.name}`,
      onSelect: async instrument => {
        try {
          const current = this.currentWatchlist(panel);
          const previousRowIds = new Set(current.rows.map(row => row.id));
          const updated = await this.repository.addItem(current, instrument.instrumentId);
          const normalized = this.updateWatchlist(updated);
          const addedRow = normalized.rows.find(row => !previousRowIds.has(row.id))
            || normalized.rows.filter(row => row.rowType === "Symbol").at(-1);
          if (!addedRow) {
            throw new Error("The symbol was added, but its watchlist row could not be selected.");
          }
          this.setSelectedRow(panel, addedRow.id, addedRow.instrumentId);
          this.renderPanelsForWatchlist(normalized.id);
          const row = this.focusAndRevealRow(panel, addedRow.id, {
            activate: true,
            flash: true
          });
          this.announce(panel, `${instrument.code} added to ${normalized.name}.`);
          return { accepted: true, focusTarget: row || trigger };
        } catch (error) {
          await this.reconcilePolicyFailure(error);
          const message = watchlistErrorMessage(error);
          this.announce(panel, message);
          return { accepted: false, message };
        }
      }
    });
  }

  nextWatchlistName() {
    const names = new Set(
      [...this.watchlists.values()].map(watchlist => watchlist.name.toLocaleLowerCase())
    );
    let index = this.watchlists.size + 1;
    let name = `Watchlist ${index}`;
    while (names.has(name.toLocaleLowerCase())) {
      index += 1;
      name = `Watchlist ${index}`;
    }
    return name;
  }

  async createWatchlist(name) {
    if (!name) return;
    try {
      const created = normalizeWatchlist(await this.repository.create(name));
      this.watchlists.set(created.id, created);
      this.openPanel(created.id);
      this.onAnnounce(`${created.name} created.`);
      return created;
    } catch (error) {
      this.onAnnounce(error.message);
      return null;
    }
  }

  async renameWatchlist(panel) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    this.closeMenus();
    const name = window.prompt("Rename watchlist", watchlist.name)?.trim();
    if (!name || name === watchlist.name) return;
    try {
      const updated = await this.repository.rename({ ...watchlist, name });
      this.updateWatchlist(updated);
      this.renderPanelsForWatchlist(updated.id);
      this.onAnnounce(`Watchlist renamed to ${updated.name}.`);
    } catch (error) {
      this.announce(panel, error.message);
    }
  }

  async deleteWatchlist(panel) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    this.closeMenus();
    if (!window.confirm(`Delete "${watchlist.name}"? This cannot be undone.`)) return;
    try {
      await this.repository.delete(watchlist);
      const affected = [...this.panels.values()]
        .filter(candidate => candidate.watchlistId === watchlist.id);
      affected.forEach(candidate => this.closePanel(candidate));
      this.watchlists.delete(watchlist.id);
      const first = this.watchlists.values().next().value;
      if (first && this.panels.size === 0) this.openPanel(first.id);
      this.onAnnounce(`${watchlist.name} deleted.`);
    } catch (error) {
      this.announce(panel, error.message);
    }
  }

  openRowMenu(panel, event, explicitRow = null) {
    const row = explicitRow || event.target.closest("tr[data-watchlist-row-kind]");
    event.preventDefault();
    this.closeMenus();
    const kind = row?.dataset.watchlistRowKind || "body";
    const rowId = row?.dataset.watchlistRowId || null;
    const model = rowId
      ? this.currentWatchlist(panel)?.rows.find(candidate => candidate.id === rowId)
      : null;
    if (row && !model) return;
    panel.rowMenuTarget = {
      kind,
      rowId,
      instrumentId: row?.dataset.instrumentId || null,
      model
    };
    panel.rowMenuReturnFocus = row || panel.element;
    row?.classList.add("is-menu-target");
    panel.rowMenu.setAttribute(
      "aria-label",
      row ? "Watchlist row actions" : "Watchlist actions"
    );
    const flagAction = panel.rowMenu.querySelector('[data-watchlist-row-action="flag"]');
    const renameAction = panel.rowMenu.querySelector(
      '[data-watchlist-row-action="rename-section"]'
    );
    const deleteAction = panel.rowMenu.querySelector(
      '[data-watchlist-row-action="delete-row"]'
    );
    flagAction.hidden = kind !== "symbol";
    renameAction.hidden = kind !== "section";
    deleteAction.hidden = !row;
    flagAction.setAttribute("aria-expanded", "false");
    for (const colorAction of panel.flagMenu.querySelectorAll("[data-watchlist-flag-color]")) {
      colorAction.setAttribute(
        "aria-checked",
        String(
          (colorAction.dataset.watchlistFlagColor || "")
          === (model?.flagColor || "")
        )
      );
    }
    panel.rowMenu.hidden = false;
    const panelRect = panel.element.getBoundingClientRect();
    const targetRect = row?.getBoundingClientRect() || panel.body.getBoundingClientRect();
    const menuRect = panel.rowMenu.getBoundingClientRect();
    const pointerX = Number(event.clientX) || targetRect.right;
    const pointerY = Number(event.clientY) || targetRect.bottom;
    panel.rowMenu.style.left = `${Math.max(8, Math.min(
      panelRect.width - menuRect.width - 8,
      pointerX - panelRect.left
    ))}px`;
    panel.rowMenu.style.top = `${Math.max(38, Math.min(
      panelRect.height - menuRect.height - 8,
      pointerY - panelRect.top
    ))}px`;
    this.rowMenuItems(panel)[0]?.focus();
  }

  handleRowMenuKeydown(panel, event) {
    if (event.key === "Escape") {
      event.preventDefault();
      const returnFocus = panel.rowMenuReturnFocus;
      this.closeMenus();
      returnFocus?.focus();
      return;
    }
    if (
      event.key === "ArrowRight"
      && document.activeElement?.dataset.watchlistRowAction === "flag"
    ) {
      event.preventDefault();
      this.openFlagMenu(panel);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = this.rowMenuItems(panel);
    if (items.length === 0) return;
    if (event.key === "Home") {
      items[0].focus();
      return;
    }
    if (event.key === "End") {
      items.at(-1).focus();
      return;
    }
    const currentIndex = Math.max(0, items.indexOf(document.activeElement));
    const delta = event.key === "ArrowDown" ? 1 : -1;
    items[(currentIndex + delta + items.length) % items.length].focus();
  }

  rowMenuItems(panel) {
    return [...panel.rowMenu.querySelectorAll("[data-watchlist-row-action]")]
      .filter(item => !item.disabled && !item.hidden);
  }

  handleRowMenuAction(panel, action) {
    if (action === "flag") {
      this.openFlagMenu(panel);
      return;
    }
    if (action === "add-section") {
      this.addSectionRow(panel);
      return;
    }
    if (action === "rename-section") {
      const rowId = panel.rowMenuTarget?.rowId;
      this.closeMenus();
      if (rowId) this.beginSectionRename(panel, rowId);
      return;
    }
    if (action === "delete-row") {
      this.deleteContextRow(panel);
      return;
    }
    if (action === "settings") {
      this.openSettings(panel);
    }
  }

  openFlagMenu(panel, focusSelection = true) {
    if (panel.rowMenuTarget?.kind !== "symbol") return;
    this.cancelFlagMenuClose(panel);
    const flagAction = panel.rowMenu.querySelector('[data-watchlist-row-action="flag"]');
    panel.flagMenu.hidden = false;
    flagAction.setAttribute("aria-expanded", "true");
    panel.flagMenu.classList.remove("is-flipped");
    const menuRect = panel.flagMenu.getBoundingClientRect();
    if (menuRect.right > window.innerWidth - 8) {
      panel.flagMenu.classList.add("is-flipped");
    }
    if (focusSelection) {
      panel.flagMenu.querySelector("[aria-checked='true']")
        ?.focus({ preventScroll: true });
    }
  }

  closeFlagMenu(panel) {
    this.cancelFlagMenuClose(panel);
    panel.flagMenu.hidden = true;
    panel.rowMenu.querySelector('[data-watchlist-row-action="flag"]')
      ?.setAttribute("aria-expanded", "false");
  }

  scheduleFlagMenuClose(panel) {
    this.cancelFlagMenuClose(panel);
    panel.flagMenuCloseTimer = window.setTimeout(() => {
      panel.flagMenuCloseTimer = null;
      this.closeFlagMenu(panel);
    }, 180);
  }

  cancelFlagMenuClose(panel) {
    if (panel.flagMenuCloseTimer === null) return;
    window.clearTimeout(panel.flagMenuCloseTimer);
    panel.flagMenuCloseTimer = null;
  }

  handleFlagMenuKeydown(panel, event) {
    if (event.key === "Escape" || event.key === "ArrowLeft") {
      event.preventDefault();
      this.closeFlagMenu(panel);
      panel.rowMenu.querySelector('[data-watchlist-row-action="flag"]')?.focus();
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = [...panel.flagMenu.querySelectorAll("[data-watchlist-flag-color]")];
    if (event.key === "Home") return items[0]?.focus();
    if (event.key === "End") return items.at(-1)?.focus();
    const currentIndex = Math.max(0, items.indexOf(document.activeElement));
    const delta = event.key === "ArrowDown" ? 1 : -1;
    items[(currentIndex + delta + items.length) % items.length]?.focus();
  }

  async setContextFlag(panel, flagColor) {
    const watchlist = this.currentWatchlist(panel);
    const rowId = panel.rowMenuTarget?.rowId;
    this.closeMenus();
    if (!watchlist || !rowId) return;
    try {
      const updated = await this.repository.updateRow(watchlist, rowId, {
        flagColor: flagColor || null
      });
      this.updateWatchlist(updated);
      this.renderPanelsForWatchlist(updated.id);
      this.focusAndRevealRow(panel, rowId);
      this.announce(panel, flagColor ? `${capitalize(flagColor)} flag applied.` : "Flag removed.");
    } catch (error) {
      this.announce(panel, error.message);
    }
  }

  async addSectionRow(panel) {
    const watchlist = this.currentWatchlist(panel);
    const afterRowId = panel.rowMenuTarget?.rowId || null;
    this.closeMenus();
    if (!watchlist) return;
    const priorIds = new Set(watchlist.rows.map(row => row.id));
    try {
      const updated = await this.repository.addRow(
        watchlist,
        "Section",
        afterRowId,
        "New section"
      );
      const normalized = this.updateWatchlist(updated);
      const addedRow = normalized.rows.find(row => !priorIds.has(row.id));
      if (!addedRow) throw new Error("The new watchlist row could not be located.");
      this.renderPanelsForWatchlist(normalized.id);
      const element = panel.rows.querySelector(
        `tr[data-watchlist-row-id="${CSS.escape(addedRow.id)}"]`
      );
      element?.scrollIntoView({ block: "nearest" });
      this.beginSectionRename(panel, addedRow.id, { cancelDeletes: true });
    } catch (error) {
      this.announce(panel, error.message);
    }
  }

  beginSectionRename(panel, rowId, { cancelDeletes = false } = {}) {
    const watchlist = this.currentWatchlist(panel);
    const model = watchlist?.rows.find(row => row.id === rowId && row.rowType === "Section");
    const row = panel.rows.querySelector(
      `tr[data-watchlist-row-id="${CSS.escape(rowId)}"]`
    );
    const content = row?.querySelector(".watchlist-section-content");
    if (!model || !content) return;
    const label = content.querySelector(".watchlist-section-name");
    const input = document.createElement("input");
    input.className = "watchlist-section-input";
    input.value = model.sectionName;
    input.maxLength = 80;
    input.setAttribute("aria-label", "Section name");
    label.replaceWith(input);
    input.focus();
    input.select();
    let finished = false;
    const finish = async commit => {
      if (finished) return;
      const name = input.value.trim();
      if (commit && !name) {
        input.setCustomValidity("Enter a section name.");
        input.reportValidity();
        return;
      }
      finished = true;
      if (!commit) {
        if (cancelDeletes) {
          await this.deleteRowById(panel, rowId, { offerUndo: false });
        } else {
          this.renderRows(panel);
        }
        return;
      }
      try {
        const current = this.currentWatchlist(panel);
        const updated = await this.repository.updateRow(current, rowId, {
          sectionName: name
        });
        this.updateWatchlist(updated);
        this.renderPanelsForWatchlist(updated.id);
        this.focusAndRevealRow(panel, rowId);
        this.announce(panel, `Section renamed to ${name}.`);
      } catch (error) {
        finished = false;
        input.setCustomValidity(error.message);
        input.reportValidity();
      }
    };
    input.addEventListener("keydown", event => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        finish(true);
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        finish(false);
      }
    });
    input.addEventListener("blur", () => finish(true), { once: true });
  }

  async toggleSection(panel, rowId, collapsed) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    try {
      const updated = await this.repository.updateRow(watchlist, rowId, {
        isCollapsed: collapsed
      });
      this.updateWatchlist(updated);
      this.renderPanelsForWatchlist(updated.id);
      this.focusAndRevealRow(panel, rowId);
    } catch (error) {
      this.announce(panel, error.message);
    }
  }

  deleteContextRow(panel) {
    const rowId = panel.rowMenuTarget?.rowId;
    this.closeMenus();
    if (rowId) this.deleteRowById(panel, rowId);
  }

  async deleteRowById(panel, rowId, { offerUndo = true } = {}) {
    const watchlist = this.currentWatchlist(panel);
    const ordered = [...(watchlist?.rows || [])].sort((a, b) => a.manualOrder - b.manualOrder);
    const index = ordered.findIndex(row => row.id === rowId);
    const deleted = ordered[index];
    if (!watchlist || !deleted) return;
    try {
      const updated = await this.repository.removeRow(watchlist, rowId);
      this.updateWatchlist(updated);
      if (panel.selectedRowId === rowId) {
        panel.selectedRowId = null;
        panel.selectedInstrumentId = null;
      }
      this.renderPanelsForWatchlist(updated.id);
      const fallbackRows = [...panel.rows.querySelectorAll("tr[data-watchlist-row-id]")];
      const fallback = fallbackRows[Math.max(0, Math.min(index, fallbackRows.length - 1))];
      if (fallback) fallback.focus();
      else panel.bottomAddButton.focus();
      if (offerUndo) this.showUndo(panel, deleted, index);
      this.announce(panel, deleted.rowType === "Section"
        ? "Section removed; its rows remain in the watchlist."
        : "Row deleted.");
    } catch (error) {
      this.announce(panel, error.message);
    }
  }

  showUndo(panel, row, index) {
    window.clearTimeout(panel.undoTimer);
    panel.undoState = { row, index };
    requiredElement("[data-watchlist-undo-message]", panel.undoToast).textContent =
      row.rowType === "Symbol" ? `${row.symbol} removed.` : "Row deleted.";
    panel.undoToast.hidden = false;
    panel.undoTimer = window.setTimeout(() => {
      panel.undoToast.hidden = true;
      panel.undoState = null;
    }, 6_000);
  }

  async undoDelete(panel) {
    const state = panel.undoState;
    const watchlist = this.currentWatchlist(panel);
    if (!state || !watchlist) return;
    if (state.row.rowType === "Symbol") {
      const quotaReason = this.watchlistQuotaReason(watchlist);
      if (quotaReason) {
        this.announce(panel, quotaReason);
        this.syncPanelQuota(panel);
        return;
      }
    }
    window.clearTimeout(panel.undoTimer);
    try {
      const updated = await this.repository.restoreRow(watchlist, state.row, state.index);
      this.updateWatchlist(updated);
      panel.undoToast.hidden = true;
      panel.undoState = null;
      this.renderPanelsForWatchlist(updated.id);
      this.focusAndRevealRow(panel, state.row.id, {
        activate: state.row.rowType === "Symbol"
      });
      this.announce(panel, "Row restored.");
    } catch (error) {
      await this.reconcilePolicyFailure(error);
      this.announce(panel, watchlistErrorMessage(error));
    }
  }

  openSettings(panel) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    for (const otherPanel of this.panels.values()) {
      if (otherPanel !== panel && !otherPanel.settingsDialog.hidden) {
        this.closeSettings(otherPanel, { restoreFocus: false });
      }
    }
    panel.settingsReturnFocus = panel.rowMenuReturnFocus || panel.element;
    this.closeMenus();
    const dialog = panel.settingsDialog;
    panel.element.classList.add("has-watchlist-settings");
    const settings = watchlist.settings;
    requiredElement("[data-watchlist-settings-name]", dialog).value = watchlist.name;
    requiredElement("[data-watchlist-settings-symbol]", dialog).checked = settings.showSymbol;
    requiredElement("[data-watchlist-settings-last]", dialog).checked = settings.showLast;
    requiredElement("[data-watchlist-settings-change]", dialog).checked = settings.showChange;
    requiredElement("[data-watchlist-settings-percent]", dialog).checked =
      settings.showPercentChange;
    requiredElement("[data-watchlist-settings-volume]", dialog).checked =
      settings.showVolume;
    requiredElement("[data-watchlist-settings-column-name]", dialog).checked =
      settings.showName;
    requiredElement("[data-watchlist-settings-sector]", dialog).checked =
      settings.showSector;
    requiredElement("[data-watchlist-settings-industry]", dialog).checked =
      settings.showIndustry;
    const positiveColor = requiredElement(
      "[data-watchlist-settings-positive-color]",
      dialog
    );
    const negativeColor = requiredElement(
      "[data-watchlist-settings-negative-color]",
      dialog
    );
    positiveColor.value = settings.positiveColor;
    negativeColor.value = settings.negativeColor;
    syncChartColorPicker(positiveColor);
    syncChartColorPicker(negativeColor);
    const density = dialog.querySelector(
      `[data-watchlist-density][value="${CSS.escape(settings.density)}"]`
    );
    if (density) density.checked = true;
    requiredElement("[data-watchlist-settings-error]", dialog).hidden = true;
    dialog.hidden = false;
    this.positionSettings(panel);
    dialog.focus();
  }

  closeSettings(panel, { restoreFocus = true } = {}) {
    if (panel.settingsDialog.hidden) return;
    this.cancelSettingsDrag(panel);
    if (panel.settingsRevision > 0) void this.flushSettings(panel);
    panel.settingsDialog.hidden = true;
    panel.element.classList.remove("has-watchlist-settings");
    if (restoreFocus) panel.settingsReturnFocus?.focus?.({ preventScroll: true });
    panel.settingsReturnFocus = null;
  }

  resetSettingsDraft(panel) {
    const dialog = panel.settingsDialog;
    requiredElement("[data-watchlist-settings-symbol]", dialog).checked = true;
    requiredElement("[data-watchlist-settings-last]", dialog).checked = true;
    requiredElement("[data-watchlist-settings-change]", dialog).checked = true;
    requiredElement("[data-watchlist-settings-percent]", dialog).checked = true;
    requiredElement("[data-watchlist-settings-volume]", dialog).checked = false;
    requiredElement("[data-watchlist-settings-column-name]", dialog).checked = false;
    requiredElement("[data-watchlist-settings-sector]", dialog).checked = false;
    requiredElement("[data-watchlist-settings-industry]", dialog).checked = false;
    const positiveColor = requiredElement(
      "[data-watchlist-settings-positive-color]",
      dialog
    );
    const negativeColor = requiredElement(
      "[data-watchlist-settings-negative-color]",
      dialog
    );
    positiveColor.value = "#35d19d";
    negativeColor.value = "#f44f6b";
    syncChartColorPicker(positiveColor);
    syncChartColorPicker(negativeColor);
    const compact = dialog.querySelector('[data-watchlist-density][value="Compact"]');
    if (compact) compact.checked = true;
    this.previewSettings(panel);
  }

  handleSettingsKeydown(panel, event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeSettings(panel);
    }
  }

  positionSettings(panel) {
    const dialog = panel.settingsDialog;
    const panelBounds = panel.element.getBoundingClientRect();
    const dialogBounds = dialog.getBoundingClientRect();
    const inset = 12;
    let left = panelBounds.right + 10;
    if (left + dialogBounds.width > window.innerWidth - inset) {
      left = panelBounds.left - dialogBounds.width - 10;
    }
    left = clamp(left, inset, Math.max(inset, window.innerWidth - dialogBounds.width - inset));
    const top = clamp(
      panelBounds.top,
      inset,
      Math.max(inset, window.innerHeight - dialogBounds.height - inset)
    );
    dialog.style.left = `${Math.round(left)}px`;
    dialog.style.top = `${Math.round(top)}px`;
  }

  startSettingsDrag(panel, event) {
    if (event.button !== 0 || event.target.closest("button, input, select, textarea, a")) {
      return;
    }
    const bounds = panel.settingsDialog.getBoundingClientRect();
    panel.settingsDrag = {
      pointerId: event.pointerId,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top
    };
    panel.settingsDialog.classList.add("is-dragging");
    panel.settingsDragHandle.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  moveSettingsDrag(panel, event) {
    const drag = panel.settingsDrag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const bounds = panel.settingsDialog.getBoundingClientRect();
    const inset = 8;
    const left = clamp(
      event.clientX - drag.offsetX,
      inset,
      Math.max(inset, window.innerWidth - bounds.width - inset)
    );
    const top = clamp(
      event.clientY - drag.offsetY,
      inset,
      Math.max(inset, window.innerHeight - bounds.height - inset)
    );
    panel.settingsDialog.style.left = `${Math.round(left)}px`;
    panel.settingsDialog.style.top = `${Math.round(top)}px`;
    event.preventDefault();
  }

  stopSettingsDrag(panel, event) {
    const drag = panel.settingsDrag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (panel.settingsDragHandle.hasPointerCapture(event.pointerId)) {
      panel.settingsDragHandle.releasePointerCapture(event.pointerId);
    }
    panel.settingsDrag = null;
    panel.settingsDialog.classList.remove("is-dragging");
  }

  cancelSettingsDrag(panel) {
    const drag = panel.settingsDrag;
    if (drag && panel.settingsDragHandle.hasPointerCapture(drag.pointerId)) {
      panel.settingsDragHandle.releasePointerCapture(drag.pointerId);
    }
    panel.settingsDrag = null;
    panel.settingsDialog.classList.remove("is-dragging");
  }

  readSettingsDraft(panel) {
    const dialog = panel.settingsDialog;
    const watchlist = this.currentWatchlist(panel);
    const nameInput = requiredElement("[data-watchlist-settings-name]", dialog);
    const name = nameInput.value.trim();
    const density = dialog.querySelector("[data-watchlist-density]:checked")?.value
      || "Compact";
    return {
      name,
      density,
      showSymbol: requiredElement("[data-watchlist-settings-symbol]", dialog).checked,
      showLast: requiredElement("[data-watchlist-settings-last]", dialog).checked,
      showChange: requiredElement("[data-watchlist-settings-change]", dialog).checked,
      showPercentChange: requiredElement("[data-watchlist-settings-percent]", dialog).checked,
      showSectionCounts: watchlist?.settings.showSectionCounts !== false,
      showVolume: requiredElement("[data-watchlist-settings-volume]", dialog).checked,
      showName: requiredElement("[data-watchlist-settings-column-name]", dialog).checked,
      showSector: requiredElement("[data-watchlist-settings-sector]", dialog).checked,
      showIndustry: requiredElement("[data-watchlist-settings-industry]", dialog).checked,
      positiveColor: requiredElement(
        "[data-watchlist-settings-positive-color]",
        dialog
      ).value,
      negativeColor: requiredElement(
        "[data-watchlist-settings-negative-color]",
        dialog
      ).value
    };
  }

  previewSettings(panel) {
    if (panel.settingsDialog.hidden) return;
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist) return;
    const draft = this.readSettingsDraft(panel);
    const error = requiredElement("[data-watchlist-settings-error]", panel.settingsDialog);
    if (!draft.name) {
      error.textContent = "Enter a watchlist name.";
      error.hidden = false;
      return;
    }
    error.hidden = true;
    panel.settingsRevision += 1;
    this.watchlists.set(watchlist.id, normalizeWatchlist({
      ...watchlist,
      name: draft.name,
      settings: {
        ...watchlist.settings,
        ...draft
      }
    }));
    this.renderPanelsForWatchlist(watchlist.id);
    this.emitObjectTreeChange({ workspaceChanged: false });
    this.scheduleSettingsSave(panel);
  }

  scheduleSettingsSave(panel, delay = 320) {
    window.clearTimeout(panel.settingsSaveTimer);
    panel.settingsSaveTimer = window.setTimeout(() => {
      panel.settingsSaveTimer = null;
      void this.flushSettings(panel);
    }, delay);
  }

  flushSettings(panel) {
    window.clearTimeout(panel.settingsSaveTimer);
    panel.settingsSaveTimer = null;
    const run = async () => {
      const watchlist = this.currentWatchlist(panel);
      if (!watchlist) return;
      const draft = this.readSettingsDraft(panel);
      if (!draft.name) return;
      const revision = panel.settingsRevision;
      const error = requiredElement("[data-watchlist-settings-error]", panel.settingsDialog);
      try {
        const updated = normalizeWatchlist(
          await this.repository.saveSettings(watchlist, draft)
        );
        if (panel.settingsRevision === revision) {
          this.watchlists.set(updated.id, updated);
          panel.settingsRevision = 0;
        } else {
          const liveDraft = this.readSettingsDraft(panel);
          this.watchlists.set(updated.id, normalizeWatchlist({
            ...updated,
            name: liveDraft.name || updated.name,
            settings: { ...updated.settings, ...liveDraft }
          }));
          this.scheduleSettingsSave(panel, 0);
        }
        this.renderPanelsForWatchlist(updated.id);
        this.emitObjectTreeChange({ workspaceChanged: false });
        error.hidden = true;
      } catch (saveError) {
        error.textContent = saveError.message;
        error.hidden = false;
      }
    };
    panel.settingsSaveChain = panel.settingsSaveChain.then(run, run);
    return panel.settingsSaveChain;
  }

  async commitAndCloseSettings(panel) {
    await this.flushSettings(panel);
    if (requiredElement(
      "[data-watchlist-settings-error]",
      panel.settingsDialog
    ).hidden) {
      panel.settingsDialog.hidden = true;
      panel.element.classList.remove("has-watchlist-settings");
      panel.settingsReturnFocus?.focus?.({ preventScroll: true });
      panel.settingsReturnFocus = null;
      this.announce(panel, "Watchlist settings saved.");
    }
  }

  startDrag(panel, item, event) {
    const watchlist = this.currentWatchlist(panel);
    if (!watchlist || panel.sortDirection !== "None") {
      event.preventDefault();
      return;
    }
    this.dragPayload = {
      panelId: panel.id,
      watchlistId: watchlist.id,
      rowId: item.id,
      rowType: item.rowType,
      instrumentId: item.instrumentId || null
    };
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", item.id);
    const row = event.currentTarget.closest("tr");
    row?.classList.add("is-dragging");
    row?.querySelector(".watchlist-drag-grip")?.setAttribute("aria-grabbed", "true");
    this.createDragPreview(item, event.dataTransfer);
    panel.element.classList.add("is-drag-source");
  }

  allowDrop(panel, event, target) {
    if (!this.dragPayload) return;
    const sourcePanel = this.panels.get(this.dragPayload.panelId);
    const sameWatchlist = this.dragPayload.watchlistId === panel.watchlistId;
    if (sameWatchlist && (
      panel.sortDirection !== "None" || sourcePanel?.sortDirection !== "None"
    )) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    for (const candidate of this.surface.querySelectorAll(".is-drop-target")) {
      if (candidate !== target) candidate.classList.remove("is-drop-target");
    }
    for (const candidate of this.surface.querySelectorAll(".is-drop-receiving")) {
      if (candidate !== panel.element) candidate.classList.remove("is-drop-receiving");
    }
    target.classList.add("is-drop-target");
    panel.element.classList.add("is-drop-receiving");
    const body = panel.element.querySelector(".watchlist-body");
    if (body) {
      const bounds = body.getBoundingClientRect();
      if (event.clientY < bounds.top + 36) body.scrollBy({ top: -24 });
      if (event.clientY > bounds.bottom - 36) body.scrollBy({ top: 24 });
    }
  }

  async dropItem(panel, event, targetIndex) {
    event.preventDefault();
    const payload = this.dragPayload;
    this.clearDropStyles();
    if (!payload) return;
    const source = this.watchlists.get(payload.watchlistId);
    const target = this.currentWatchlist(panel);
    if (!source || !target) return;
    if (source.id !== target.id && payload.rowType === "Symbol") {
      const quotaReason = this.watchlistQuotaReason(target);
      if (quotaReason) {
        this.announce(panel, quotaReason);
        this.syncPanelQuota(panel);
        this.dragPayload = null;
        return;
      }
    }
    const effectiveTargetIndex = source.id !== target.id
      && panel.sortDirection !== "None"
      ? target.rows.length
      : targetIndex;
    try {
      const result = await this.repository.moveRow(
        source,
        target,
        payload.rowId,
        effectiveTargetIndex
      );
      this.updateWatchlist(result.source);
      this.updateWatchlist(result.target);
      panel.sortColumn = "Manual";
      panel.sortDirection = "None";
      if (payload.rowType === "Symbol") {
        this.setSelectedRow(panel, payload.rowId, payload.instrumentId);
      }
      this.renderPanelsForWatchlist(result.source.id);
      if (result.target.id !== result.source.id) {
        this.renderPanelsForWatchlist(result.target.id);
      }
      this.focusAndRevealRow(panel, payload.rowId);
      this.announce(panel, result.target.id === result.source.id
        ? "Watchlist order updated."
        : `Symbol moved to ${result.target.name}.`);
    } catch (error) {
      await this.reconcilePolicyFailure(error);
      this.announce(panel, watchlistErrorMessage(error));
    } finally {
      this.dragPayload = null;
    }
  }

  endDrag(panel = null, rowId = null) {
    if (panel && rowId) {
      panel.suppressClickRowId = rowId;
      window.setTimeout(() => {
        if (panel.suppressClickRowId === rowId) {
          panel.suppressClickRowId = null;
        }
      }, 0);
    }
    this.dragPayload = null;
    this.clearDropStyles();
  }

  createDragPreview(item, dataTransfer) {
    this.dragPreview?.remove();
    const preview = document.createElement("div");
    preview.className = "watchlist-drag-preview";

    const grip = document.createElement("span");
    grip.className = "watchlist-drag-preview-grip";
    grip.setAttribute("aria-hidden", "true");
    for (let index = 0; index < 6; index += 1) {
      grip.append(document.createElement("i"));
    }

    const copy = document.createElement("span");
    copy.className = "watchlist-drag-preview-copy";
    const symbol = document.createElement("strong");
    symbol.textContent = item.rowType === "Section"
      ? item.sectionName
      : item.rowType === "Empty" ? "Empty row" : item.symbol;
    const exchange = document.createElement("span");
    exchange.textContent = item.rowType === "Symbol"
      ? item.exchangeDisplayName || item.exchangeCode
      : item.rowType;
    copy.append(symbol, exchange);

    const badge = document.createElement("span");
    badge.className = "watchlist-drag-preview-badge";
    badge.textContent = "Move";

    preview.append(grip, copy, badge);
    document.body.append(preview);
    this.dragPreview = preview;
    if (typeof dataTransfer?.setDragImage === "function") {
      dataTransfer.setDragImage(preview, 18, 18);
    }
  }

  clearDropStyles() {
    for (const element of this.surface.querySelectorAll(
      ".is-dragging, .is-drag-source, .is-drop-receiving, .is-drop-target"
    )) {
      element.classList.remove(
        "is-dragging",
        "is-drag-source",
        "is-drop-receiving",
        "is-drop-target"
      );
    }
    for (const grip of this.surface.querySelectorAll(
      '.watchlist-drag-grip[aria-grabbed="true"]'
    )) {
      grip.removeAttribute("aria-grabbed");
    }
    this.dragPreview?.remove();
    this.dragPreview = null;
  }

  updateWatchlist(watchlist) {
    const normalized = normalizeWatchlist(watchlist);
    this.watchlists.set(normalized.id, normalized);
    this.emitObjectTreeChange();
    return normalized;
  }

  subscribeObjectTree(listener) {
    if (typeof listener !== "function") return () => {};
    this.objectTreeListeners.add(listener);
    return () => this.objectTreeListeners.delete(listener);
  }

  objectTreePanels() {
    return [...this.panels.values()].map((panel) => {
      const watchlist = this.currentWatchlist(panel);
      return {
        id: panel.id,
        label: watchlist?.name || "Watchlist",
        description: `${watchlist?.items?.length || 0} symbols`,
        stackOrder: panel.stackOrder,
        active: panel.element.classList.contains("is-active"),
        status: capitalize(panel.floating.state)
      };
    });
  }

  focusPanelById(panelId) {
    const panel = this.panels.get(panelId);
    if (!panel) return false;
    if (panel.floating.state === "minimized") {
      panel.floating.restore();
      this.updateWindowControls(panel);
      this.scheduleWorkspaceSave();
    }
    this.focusPanel(panel);
    panel.element.focus({ preventScroll: true });
    return true;
  }

  openSettingsById(panelId) {
    const panel = this.panels.get(panelId);
    if (!panel) return false;
    this.focusPanelById(panelId);
    this.openSettings(panel);
    return true;
  }

  async deleteWatchlistByPanelId(panelId) {
    const panel = this.panels.get(panelId);
    if (!panel) return false;
    await this.deleteWatchlist(panel);
    return true;
  }

  emitObjectTreeChange({ workspaceChanged = true } = {}) {
    if (workspaceChanged) this.onWorkspaceChange();
    for (const listener of [...this.objectTreeListeners]) listener();
  }

  renderPanelsForWatchlist(watchlistId) {
    for (const panel of this.panels.values()) {
      if (panel.watchlistId === watchlistId) this.renderPanel(panel);
    }
    this.scheduleWorkspaceSave();
  }

  watchlistQuotaReason(watchlist, additional = 1) {
    const maximum = this.maximumItemsPerWatchlist;
    if (!Number.isFinite(maximum)) return null;
    if (maximum === 0) return "This product does not include watchlist items.";
    const current = (watchlist?.rows || []).filter((row) => row.rowType === "Symbol").length;
    return current + Math.max(0, Number(additional) || 0) > maximum
      ? "You have reached the watchlist item limit for this product."
      : null;
  }

  syncPanelQuota(panel) {
    const reason = this.watchlistQuotaReason(this.currentWatchlist(panel));
    for (const button of [panel?.bottomAddButton, panel?.emptyAddButton]) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = Boolean(reason);
      button.title = reason || "Add symbol";
      button.setAttribute(
        "aria-label",
        reason ? `Add symbol unavailable. ${reason}` : "Add symbol"
      );
    }
  }

  async reconcilePolicyFailure(error) {
    if (!isWatchlistPolicyError(error)) return;
    try {
      this.replaceWatchlists(await this.repository.list());
      for (const panel of this.panels.values()) this.renderPanel(panel);
    } catch {
      // The original product-policy failure remains the actionable message.
    }
  }

  updateWindowControls(panel) {
    const floating = panel.floating.state === "floating";
    panel.minimizeButton.hidden = !floating;
    panel.maximizeButton.hidden = !floating;
    panel.restoreButton.hidden = floating;
  }

  scheduleWorkspaceSave() {
    if (this.destroyed) return;
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.queueWorkspaceSave(), 450);
  }

  queueWorkspaceSave() {
    const payload = {
      version: this.workspace.version,
      panels: [...this.panels.values()].map(panel => {
        const geometry = panel.floating.state === "floating"
          ? panel.floating.snapshot()
          : panel.floating.restoreGeometry || panel.floating.snapshot();
        return {
          id: panel.id,
          watchlistId: panel.watchlistId,
          x: geometry.x,
          y: geometry.y,
          width: geometry.width,
          height: geometry.height,
          windowState: capitalize(panel.floating.state),
          sortColumn: panel.sortColumn,
          sortDirection: panel.sortDirection,
          selectedInstrumentId: panel.selectedInstrumentId,
          selectedRowId: panel.selectedRowId,
          stackOrder: panel.stackOrder
        };
      })
    };
    this.saveChain = this.saveChain
      .then(async () => {
        payload.version = this.workspace.version;
        this.workspace = normalizeWorkspace(await this.repository.saveWorkspace(payload));
      })
      .catch(async error => {
        if (error.status === 409) {
          this.workspace = normalizeWorkspace(await this.repository.getWorkspace());
          this.scheduleWorkspaceSave();
          return;
        }
        this.onAnnounce(error.message);
      });
  }

  handleDocumentPointerDown(event) {
    if (
      event.target.closest("[data-watchlist-row-menu]")
      || event.target.closest("[data-watchlist-flag-menu]")
      || event.target.closest("[data-chart-color-picker-popover]")
    ) {
      return;
    }
    for (const panel of this.panels.values()) {
      if (
        !panel.settingsDialog.hidden
        && !panel.settingsDialog.contains(event.target)
      ) {
        this.closeSettings(panel, { restoreFocus: false });
      }
    }
    this.closeMenus(true);
  }

  closeMenus(restoreFocus = false) {
    for (const panel of this.panels.values()) {
      const returnFocus = panel.rowMenuReturnFocus;
      panel.rowMenuReturnFocus?.classList?.remove("is-menu-target");
      this.closeFlagMenu(panel);
      panel.rowMenu.hidden = true;
      panel.rowMenuInstrumentId = null;
      panel.rowMenuTarget = null;
      if (restoreFocus) returnFocus?.focus?.({ preventScroll: true });
    }
  }

  announce(panel, message) {
    panel.live.textContent = "";
    requestAnimationFrame(() => {
      panel.live.textContent = message;
      this.onAnnounce(message);
    });
  }

  destroy() {
    this.destroyed = true;
    window.clearTimeout(this.saveTimer);
    document.removeEventListener("pointerdown", this.boundDocumentPointerDown, true);
    for (const panel of [...this.panels.values()]) this.closePanel(panel);
    this.objectTreeListeners.clear();
  }
}

function normalizeWorkspace(workspace) {
  return {
    version: Number(workspace?.version) || 0,
    panels: Array.isArray(workspace?.panels) ? workspace.panels : []
  };
}

function elementTrigger(panelElement, target) {
  return target instanceof HTMLElement && panelElement.contains(target)
    ? target
    : panelElement;
}

function normalizeWatchlist(watchlist) {
  const items = Array.isArray(watchlist?.items)
    ? watchlist.items.map(item => ({
        ...item,
        manualOrder: Number(item.manualOrder) || 0
      }))
    : [];
  const rows = Array.isArray(watchlist?.rows) && watchlist.rows.length > 0
    ? watchlist.rows.map(row => ({
        ...row,
        rowType: row.rowType || "Symbol",
        manualOrder: Number(row.manualOrder) || 0,
        isCollapsed: Boolean(row.isCollapsed),
        flagColor: row.flagColor || null
      }))
    : items.map(item => ({
        ...item,
        rowType: "Symbol",
        sectionName: null,
        flagColor: null,
        isCollapsed: false
      }));
  return {
    ...watchlist,
    version: Number(watchlist.version) || 1,
    items,
    rows,
    settings: {
      density: watchlist?.settings?.density || "Compact",
      showSymbol: watchlist?.settings?.showSymbol !== false,
      showLast: watchlist?.settings?.showLast !== false,
      showChange: watchlist?.settings?.showChange !== false,
      showPercentChange: watchlist?.settings?.showPercentChange !== false,
      showSectionCounts: watchlist?.settings?.showSectionCounts !== false,
      showVolume: watchlist?.settings?.showVolume === true,
      showName: watchlist?.settings?.showName === true,
      showSector: watchlist?.settings?.showSector === true,
      showIndustry: watchlist?.settings?.showIndustry === true,
      positiveColor: normalizeHexColor(
        watchlist?.settings?.positiveColor,
        "#35d19d"
      ),
      negativeColor: normalizeHexColor(
        watchlist?.settings?.negativeColor,
        "#f44f6b"
      )
    }
  };
}

function normalizedExchange(instrument) {
  return String(
    instrument.exchangeDisplayName
    || instrument.exchangeCode
    || instrument.exchange
    || ""
  ).trim().toLocaleUpperCase();
}

function toInstrument(item) {
  return {
    instrumentId: item.instrumentId,
    code: item.symbol,
    displayName: item.displayName,
    exchangeCode: item.exchangeCode,
    exchangeDisplayName: item.exchangeDisplayName,
    securityType: item.securityType,
    currency: item.currency,
    dataAvailability: item.dataAvailability,
    providerSymbol: item.providerSymbol || item.symbol,
    timeZoneId: item.timeZoneId || null,
    aliases: []
  };
}

function visibleInstrumentIds(panel, prefetchRows, maximumRows) {
  const rows = [...panel.rows.querySelectorAll("tr[data-instrument-id]")];
  if (rows.length === 0) return [];
  const viewportHeight = panel.body.clientHeight || panel.body.getBoundingClientRect().height;
  if (!(viewportHeight > 0)) return [];
  const scrollTop = panel.body.scrollTop;
  const firstCandidate = rows.findIndex(row =>
    row.offsetTop + Math.max(1, row.offsetHeight) >= scrollTop);
  const firstVisible = firstCandidate < 0 ? rows.length - 1 : firstCandidate;
  let lastVisible = firstVisible;
  while (lastVisible + 1 < rows.length &&
      rows[lastVisible + 1].offsetTop <= scrollTop + viewportHeight) {
    lastVisible += 1;
  }
  const range = visibleRowWindow(
    rows.length,
    firstVisible,
    lastVisible,
    prefetchRows,
    maximumRows
  );
  return rows.slice(range.start, range.endExclusive).map(row => row.dataset.instrumentId);
}

export function visibleRowWindow(
  rowCount,
  firstVisible,
  lastVisible,
  prefetchRows = LIVE_PREFETCH_ROWS,
  maximumRows = MAX_LIVE_WATCHLIST_ROWS
) {
  const count = Math.max(0, Number(rowCount) || 0);
  if (count === 0) return { start: 0, endExclusive: 0 };
  let start = Math.max(0, Math.min(count - 1, firstVisible) - prefetchRows);
  const desiredEnd = Math.min(count, Math.max(firstVisible, lastVisible) + 1 + prefetchRows);
  if (desiredEnd - start > maximumRows) start = Math.max(0, desiredEnd - maximumRows);
  return {
    start,
    endExclusive: desiredEnd
  };
}

function marketDataStatusDetail(status) {
  return status === "live"
    ? "Live market data."
    : status === "delayed"
      ? "Delayed market data."
      : status === "not-entitled"
        ? "Not entitled to this market-data product."
        : status === "reconnecting"
          ? "Live market data is reconnecting."
          : "Live market data is offline; the displayed snapshot may be stale.";
}

function formatPrice(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: Math.abs(numeric) < 1 ? 4 : 2,
    maximumFractionDigits: Math.abs(numeric) < 1 ? 4 : 2
  });
}

function formatSigned(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const formatted = Math.abs(numeric).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${numeric > 0 ? "+" : numeric < 0 ? "−" : ""}${formatted}`;
}

function formatVolume(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return Math.round(numeric).toLocaleString();
}

function classifyInstrument(item) {
  const symbol = String(item?.symbol || "").trim().toUpperCase();
  const known = {
    AAPL: { sector: "Technology", industry: "Consumer Electronics" },
    MSFT: { sector: "Technology", industry: "Software—Infrastructure" },
    NVDA: { sector: "Technology", industry: "Semiconductors" },
    GOOG: { sector: "Communication Services", industry: "Internet Content" },
    GOOGL: { sector: "Communication Services", industry: "Internet Content" },
    SPY: { sector: "ETF", industry: "Broad Market" }
  }[symbol];
  if (known) return known;
  if (String(item?.securityType || "").toLowerCase() === "crypto") {
    return { sector: "Digital Assets", industry: "Cryptocurrency" };
  }
  if (String(item?.securityType || "").toLowerCase() === "future") {
    return { sector: "Derivatives", industry: "Futures" };
  }
  return { sector: "—", industry: "—" };
}

function normalizeHexColor(value, fallback) {
  const candidate = String(value || "").trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(candidate) ? candidate : fallback;
}

function normalizedProblemCode(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replaceAll("_", "-")
    .toLowerCase();
}

export function watchlistPolicyMessage(code, fallback) {
  const normalized = normalizedProblemCode(code);
  if (normalized.includes("quota") && normalized.includes("watchlist")) {
    return "You have reached the watchlist item limit for this product.";
  }
  if (normalized === "quota-exceeded") {
    return "You have reached the watchlist item limit for this product.";
  }
  if (normalized === "capability-disabled") {
    return "This product does not include watchlist items.";
  }
  if (normalized === "resource-product-mismatch") {
    return "This watchlist belongs to another product.";
  }
  if (normalized === "enrollment-expired") return "Your product access has expired.";
  if (normalized === "product-inactive") return "This product is not currently available.";
  if (normalized === "product-access-denied" || normalized === "access-invalid") {
    return "You no longer have access to watchlists for this product.";
  }
  return String(fallback || "The watchlist request failed.");
}

function watchlistErrorMessage(error) {
  return watchlistPolicyMessage(error?.code, error?.message);
}

function isWatchlistPolicyError(error) {
  const code = normalizedProblemCode(error?.code);
  return code.includes("quota")
    || [
      "capability-disabled",
      "resource-product-mismatch",
      "enrollment-expired",
      "product-inactive",
      "product-access-denied",
      "access-invalid"
    ].includes(code);
}

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function capitalize(value) {
  return value ? value[0].toUpperCase() + value.slice(1) : "Floating";
}

function requiredElement(selector, root) {
  const element = root.querySelector(selector);
  if (!(element instanceof HTMLElement)) {
    throw new Error(`Required watchlist element is missing: ${selector}`);
  }
  return element;
}
